import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { DisableLinkedTaskDialogWarning } from '~src/linkedTasks/components/DisableLinkedTaskDialogWarning';

import { Messages } from '../../codegen/Localize';
import {
  ActionHelpers,
  standardActionErrorMsg,
  standardActionSuccessMsg,
  StandardActionToastMessages
} from '../../commonInfra/contentActions/ActionHelpers';
import { IErrorItem } from '../../commonInfra/contentActions/MultipleItemResult';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { L10n } from '../../ts/L10n/L10n';
import { Set } from '../../ts/models/Set';
import { SchedulesService } from '../../ts/resources/Schedules';
import { ScheduleErrorsService } from '../../ts/services/ScheduleErrors';
import { ISchedule } from '../types/ISchedule';


export namespace SetScheduleStatusActionService {

  export function enableSchedule(schedule: ISchedule): Promise<IResult> {
    return enableSchedules(singleton(schedule));
  }

  export function disableSchedule(schedule: ISchedule): Promise<IResult> {
    return disableSchedules(singleton(schedule));
  }

  export function enableLinkedTaskSchedule(schedule: ISchedule): Promise<IResult> {
    return enableLinkedTaskSchedules(singleton(schedule));
  }

  export function disableLinkedTaskSchedule(schedule: ISchedule): Promise<IResult> {
    return disableLinkedTaskSchedules(singleton(schedule));
  }

  function singleton(schedule: ISchedule): Set<ISchedule> {
    return Set.singleton(schedule, (s: ISchedule) => s.id);
  }

  export function confirmEnableSchedules(schedules: Set<ISchedule>): Promise<IResult> {
    const dialogText = schedules.size() === 1
      ? Messages.enableScheduleDialogPrompt({ NAME: schedules.getByIndex(0).name })
      : Messages.enableSchedulesDialogPrompt({ ITEM_COUNT_LOC: L10n.localizedCount(schedules.size()) });
    const actionName = schedules.size() === 1
      ? Messages.enableScheduleDialogSubmit()
      : Messages.enableSchedulesDialogSubmit({ ITEM_COUNT_LOC: L10n.localizedCount(schedules.size()) });
    return confirmAction(
      () => enableSchedules(schedules),
      Messages.enableSchedulesDialogTitle(),
      dialogText,
      actionName,
    );
  }

  export function confirmDisableSchedules(schedules: Set<ISchedule>): Promise<IResult> {
    const dialogText = schedules.size() === 1
      ? Messages.disableScheduleDialogPrompt({ NAME: schedules.getByIndex(0).name })
      : Messages.disableSchedulesDialogPrompt({ ITEM_COUNT_LOC: L10n.localizedCount(schedules.size()) });
    const actionName = schedules.size() === 1
      ? Messages.disableScheduleDialogSubmit()
      : Messages.disableSchedulesDialogSubmit({ ITEM_COUNT_LOC: L10n.localizedCount(schedules.size()) });
    return confirmAction(
      () => disableSchedules(schedules),
      Messages.disableSchedulesDialogTitle(),
      dialogText,
      actionName,
    );
  }

  export function confirmEnableLinkedTaskSchedules(schedules: Set<ISchedule>): Promise<IResult> {
    const dialogText = schedules.size() === 1
      ? Messages.linkedTasksEnableScheduleDialogPrompt({ NAME: schedules.getByIndex(0).name })
      : Messages.linkedTasksEnableSchedulesDialogPrompt({ ITEM_COUNT_LOC: L10n.localizedCount(schedules.size()) });
    const actionName = Messages.linkedTasksEnable();
    return confirmAction(
      () => enableLinkedTaskSchedules(schedules),
      Messages.linkedTasksEnable(),
      dialogText,
      actionName,
    );
  }

  export function confirmDisableLinkedTaskSchedules(schedules: Set<ISchedule>): Promise<IResult> {
    const dialogText = schedules.size() === 1
      ? Messages.linkedTasksDisableScheduleDialogPrompt({ NAME: schedules.getByIndex(0).name })
      : Messages.linkedTasksDisableSchedulesDialogPrompt({ ITEM_COUNT_LOC: L10n.localizedCount(schedules.size()) });
    const actionName = Messages.linkedTasksDisable();
    return confirmAction(
      () => disableLinkedTaskSchedules(schedules),
      Messages.linkedTasksDisable(),
      dialogText,
      actionName,
    );
  }

  function confirmAction(
    action: () => Promise<IResult>,
    title: string,
    bodyText: string,
    actionName: string,
  ) {

    const confirmStatusDialog = openReactConfirmationDialog({
      title: title,
      paramsForAction: undefined,
      message: bodyText,
      actionName: actionName,
      bodyComponent: actionName === Messages.linkedTasksDisable() ? DisableLinkedTaskDialogWarning : undefined,
      action: action,
      dialogWidth: 400
    });
    return Promise.resolve(confirmStatusDialog.result);
  }

  function enableSchedules(schedules: Set<ISchedule>): Promise<IResult> {
    const translations: StandardActionToastMessages = {
      error: standardActionErrorMsg(Messages.enableSchedulesError),
      success: standardActionSuccessMsg(Messages.enableSchedulesSuccess),
    };

    return SchedulesService.enable(schedules.keys())
      .then((result: IResult) => {
        notifyResult(schedules, result, translations);
        return result;
      });
  }

  function disableSchedules(schedules: Set<ISchedule>): Promise<IResult> {
    const translations: StandardActionToastMessages = {
      error: standardActionErrorMsg(Messages.disableSchedulesError),
      success: standardActionSuccessMsg(Messages.disableSchedulesSuccess),
    };

    return SchedulesService.disable(schedules.keys())
      .then((result: IResult) => {
        notifyResult(schedules, result, translations);
        return result;
      });
  }

  function enableLinkedTaskSchedules(schedules: Set<ISchedule>): Promise<IResult> {
    const translations: StandardActionToastMessages = {
      error: standardActionErrorMsg(Messages.linkedTasksEnableSchedulesError),
      success: standardActionSuccessMsg(Messages.linkedTasksEnableSchedulesSuccess),
    };

    return SchedulesService.enableLinkedTask(schedules.keys())
      .then((result: IResult) => {
        notifyResult(schedules, result, translations);
        return result;
      });
  }

  function disableLinkedTaskSchedules(schedules: Set<ISchedule>): Promise<IResult> {
    const translations: StandardActionToastMessages = {
      error: standardActionErrorMsg(Messages.linkedTasksDisableSchedulesError),
      success: standardActionSuccessMsg(Messages.linkedTasksDisableSchedulesSuccess),
    };

    return SchedulesService.disableLinkedTask(schedules.keys())
      .then((result: IResult) => {
        notifyResult(schedules, result, translations);
        return result;
      });
  }

  function notifyResult(schedules: Set<ISchedule>, result: IResult, translations: StandardActionToastMessages) {
    ActionHelpers.displayStandardToastsForResult(
      schedules,
      result,
      translations,
      (errorItem: IErrorItem<ISchedule>) => ScheduleErrorsService.errorDetailsFor(errorItem));
  }
}


export type SetScheduleStatusActionService = typeof SetScheduleStatusActionService;
